import { useState } from 'react';
import type { CloudPCDetail, Snapshot, OperationRecord } from '../types';

interface CloudPCDetailModalProps {
  data: CloudPCDetail;
  snapshots: Snapshot[];
  operationRecords: OperationRecord[];
  onClose: () => void;
}

export default function CloudPCDetailModal({
  data,
  snapshots,
  operationRecords,
  onClose,
}: CloudPCDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'basic' | 'monitor' | 'config' | 'snapshot' | 'records'>('basic');

  const getStatusConfig = (status: CloudPCDetail['status']) => {
    const configs = {
      running: { text: '运行中', color: 'bg-green-100 text-green-700', icon: 'ri-play-circle-fill' },
      stopped: { text: '已停止', color: 'bg-gray-100 text-gray-700', icon: 'ri-stop-circle-fill' },
      creating: { text: '创建中', color: 'bg-blue-100 text-blue-700', icon: 'ri-loader-4-line' },
      fault: { text: '故障', color: 'bg-red-100 text-red-700', icon: 'ri-error-warning-fill' },
      expired: { text: '已过期', color: 'bg-orange-100 text-orange-700', icon: 'ri-time-fill' },
    };
    return configs[status];
  };

  const statusConfig = getStatusConfig(data.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">云电脑详情</h2>
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color}`}
            >
              <i className={statusConfig.icon}></i>
              {statusConfig.text}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('basic')}
            className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
              activeTab === 'basic'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            基础信息
          </button>
          <button
            onClick={() => setActiveTab('monitor')}
            className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
              activeTab === 'monitor'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            实时监控
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
              activeTab === 'config'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            配置信息
          </button>
          <button
            onClick={() => setActiveTab('snapshot')}
            className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
              activeTab === 'snapshot'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            磁盘快照
          </button>
          <button
            onClick={() => setActiveTab('records')}
            className={`px-6 py-3 text-sm font-medium whitespace-nowrap ${
              activeTab === 'records'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            操作记录
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    云电脑ID
                  </label>
                  <p className="text-sm text-gray-900">{data.id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    云电脑商品
                  </label>
                  <p className="text-sm text-gray-900">{data.productName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    云电脑名称
                  </label>
                  <p className="text-sm text-gray-900">{data.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    所属用户
                  </label>
                  <p className="text-sm text-blue-600 cursor-pointer hover:underline">
                    {data.userName} ({data.userPhone})
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    创建时间
                  </label>
                  <p className="text-sm text-gray-900">{data.createTime}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    累计运行时长
                  </label>
                  <p className="text-sm text-gray-900">{data.totalRunningTime}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    配置规格
                  </label>
                  <p className="text-sm text-gray-900">
                    {data.productName} ({data.cpu}核/{data.memory}GB/{data.systemDisk}GB
                    {data.gpu && `/${data.gpu}`})
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    操作系统
                  </label>
                  <p className="text-sm text-gray-900">{data.os}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    镜像版本
                  </label>
                  <p className="text-sm text-gray-900">{data.imageVersion}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    内网IP / 公网IP
                  </label>
                  <p className="text-sm text-gray-900">
                    {data.internalIp} / {data.publicIp}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    所属机房
                  </label>
                  <p className="text-sm text-gray-900">{data.datacenter}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    所属服务器
                  </label>
                  <p className="text-sm text-gray-900">{data.server}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    物理服务器ID
                  </label>
                  <p className="text-sm text-gray-900">{data.physicalServerId}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    释放时间
                  </label>
                  <p className="text-sm text-gray-900">{data.releaseTime || '-'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  购买方式
                </label>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-5 gap-4 text-sm">
                    <div className="font-medium text-gray-700">商品名称</div>
                    <div className="font-medium text-gray-700">开始时间</div>
                    <div className="font-medium text-gray-700">到期时间</div>
                    <div className="font-medium text-gray-700">剩余时长</div>
                    <div className="font-medium text-gray-700">消费</div>
                  </div>
                  <div className="grid grid-cols-5 gap-4 text-sm mt-2">
                    <div className="text-gray-900">{data.purchaseDetail}</div>
                    <div className="text-gray-900">{data.createTime}</div>
                    <div className="text-gray-900">{data.expireTime}</div>
                    <div className="text-gray-900">{data.remainingTime}</div>
                    <div className="text-gray-900">¥1,280.00</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'monitor' && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6 text-center">
                <i className="ri-line-chart-line text-4xl text-gray-400 mb-2"></i>
                <p className="text-gray-600">实时监控数据将同步运维后台的监控模块</p>
                <p className="text-sm text-gray-500 mt-1">数据每10秒自动刷新</p>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPU</label>
                  <p className="text-sm text-gray-900">
                    {data.cpu}核 - {data.cpuModel}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">内存</label>
                  <p className="text-sm text-gray-900">
                    {data.memory}GB - {data.memoryType}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">磁盘</label>
                  <p className="text-sm text-gray-900">
                    {data.systemDisk}GB - {data.diskType}
                  </p>
                </div>
                {data.gpu && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GPU</label>
                    <p className="text-sm text-gray-900">
                      {data.gpu} - {data.gpuMemory}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">网络</label>
                  <p className="text-sm text-gray-900">{data.bandwidth}</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'snapshot' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  已创建 {snapshots.length} 个快照，最多支持 10 个快照
                </p>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors whitespace-nowrap">
                  <i className="ri-add-line mr-2"></i>
                  创建快照
                </button>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        快照ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        快照名称
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        快照大小
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                        创建时间
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {snapshots.map((snapshot) => (
                      <tr key={snapshot.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{snapshot.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{snapshot.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{snapshot.size}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{snapshot.createTime}</td>
                        <td className="px-4 py-3 text-right">
                          <button className="text-blue-600 hover:text-blue-700 text-sm mr-3 whitespace-nowrap">
                            恢复
                          </button>
                          <button className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap">
                            删除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'records' && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                      时间
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                      操作类型
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                      操作人
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                      操作结果
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                      备注
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {operationRecords.map((record, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{record.time}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{record.type}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{record.operator}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            record.result === '成功'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {record.result}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{record.remark}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors whitespace-nowrap">
              <i className="ri-play-line mr-2"></i>
              启动
            </button>
            <button className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition-colors whitespace-nowrap">
              <i className="ri-stop-line mr-2"></i>
              关机
            </button>
            <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors whitespace-nowrap">
              <i className="ri-shut-down-line mr-2"></i>
              强制关机
            </button>
            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors whitespace-nowrap">
              <i className="ri-restart-line mr-2"></i>
              软重启
            </button>
            <button className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors whitespace-nowrap">
              <i className="ri-restart-fill mr-2"></i>
              硬重启
            </button>
            <button className="px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700 transition-colors whitespace-nowrap">
              <i className="ri-forbid-line mr-2"></i>
              禁用
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-right-line mr-2"></i>
              迁移实例
            </button>
            <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors whitespace-nowrap">
              <i className="ri-delete-bin-line mr-2"></i>
              删除
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors whitespace-nowrap"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}
